import { Alert, StatusType } from '@platform/witems';
import {
  careUpdatePushesOf, careUpdateLastPushErrorOf, careUpdateDraftOf, careUpdateNoVisitNote,
  isCareUpdateTask, type TaskLike,
} from '../logic/careUpdate';
import { ELATION_PATIENT_BASE_URL } from '../config/medical-constants';

// Mounted on BOTH task surfaces (drawer + patient-file card) so they can never disagree.
// Precedence: a landed push outranks a stale error; no stamp at all renders nothing, since a
// "pending" line before WF70's kill switch is on would claim an automation that isn't running.
//
// ONE LINE, and it always answers the same two questions: was this drafted by AI, and did it
// reach the chart. It rode as an Alert `title` + `text` pair until 2026-08-26, which drew a
// three-line block for a status line and never said the prose was machine-written — the one fact
// a coordinator needs before they read it as a colleague's work. `text` is Alert's required prop
// and `title` is optional, so text-only is the compact variation.
export default function CareUpdateStatus({ task, ehrPatientId }: {
  task: TaskLike | null | undefined;
  ehrPatientId?: string | number | null;
}) {
  if (!isCareUpdateTask(task)) return null;

  const chartLink = ehrPatientId
    ? {
      linkText: 'Open in Elation',
      linkURL: `${ELATION_PATIENT_BASE_URL}/${ehrPatientId}/`,
      // Elation is a different system: same-tab navigation loses the drawer and the queue position.
      linkInNewTab: true,
    }
    : {};
  // Alert narrows `type` to these four — StatusType at large does not satisfy it.
  const line = (type: StatusType.INFO | StatusType.GOOD | StatusType.MODERATE | StatusType.BAD, text: string, withLink = true) => (
    <div style={{ margin: '8px 0 0' }}>
      <Alert type={type} text={text} {...(withLink ? chartLink : {})} />
    </div>
  );

  const pushes = careUpdatePushesOf(task);
  const last = pushes.length ? pushes[pushes.length - 1] : null;
  if (last) {
    const when = last.at ? String(last.at).slice(0, 10) : null;
    return line(StatusType.GOOD, `AI-drafted Care Update — sent to the Elation chart${when ? ` on ${when}` : ''}${last.note_id ? ` (note ${last.note_id})` : ''}. Review it there before sending to the PCP.`);
  }

  const err = careUpdateLastPushErrorOf(task);
  if (err) {
    return line(StatusType.BAD, `AI-drafted Care Update — NOT sent to the chart (auto-push failed: ${err.code || 'unknown'}). Compose it manually in Elation.`);
  }

  const draft = careUpdateDraftOf(task);
  // No clinician visit note in Elation ⇒ there was nothing to write the clinical narrative FROM
  // (since 2026-08-27 the visit note is the only chart-derived source). Say why, rather than
  // pointing the coordinator at a draft whose Assessment is a placeholder. Checked BEFORE the
  // status branches so a `ready` draft with stubs cannot read as a usable Care Update.
  if (careUpdateNoVisitNote(task)) {
    return line(StatusType.MODERATE, 'Care Update not generated — no clinician visit note on file in Elation to write it from. Document the visit in Elation, then compose the update there.');
  }
  if (draft?.status === 'generating') {
    return line(StatusType.INFO, 'AI-drafted Care Update — generating now; it will appear in the Elation chart shortly.', false);
  }
  // Drafted but no push recorded: either the chart already held this note (the belt skipped the
  // post) or the draft was generated without one. Both need a human to look at the chart — so
  // this says "not sent" rather than claiming a delivery nothing recorded.
  if (draft?.status === 'ready') {
    return line(StatusType.INFO, 'AI-drafted Care Update — not sent to the chart. Check Elation before completing this task.');
  }
  // Nothing was drafted, so this one must NOT claim AI authorship of a document that does not exist.
  if (draft?.status === 'error') {
    return line(StatusType.BAD, `Care Update — generation failed${draft.error ? ` (${draft.error})` : ''}. Compose it manually in Elation.`);
  }
  return null;
}
